import React from "react";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";

function SkeletonLine({ width = "100%", height = 18 }) {
  return <Skeleton variant="text" width={width} height={height} sx={{ borderRadius: "4px" }} />;
}

function SkeletonChip({ width = 72 }) {
  return (
    <Skeleton variant="rounded" width={width} height={24} sx={{ borderRadius: "20px" }} />
  );
}

/** Full team detail page (632px content column). */
export function TeamDetailPageSkeleton() {
  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Box sx={{ bgcolor: "#FFF", px: 3, py: 2, borderBottom: "1px solid #E5E9EC", flexShrink: 0 }}>
        <Box sx={{ maxWidth: 632, mx: "auto" }}>
          <Skeleton variant="text" width={200} height={28} sx={{ borderRadius: "4px" }} />
          <Skeleton variant="text" width="60%" height={18} sx={{ borderRadius: "4px", mt: 1 }} />
        </Box>
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto", pt: "32px", px: 3, pb: 3 }}>
        <Box sx={{ maxWidth: 632, width: "100%", mx: "auto" }}>
          <Skeleton
            variant="rounded"
            width="100%"
            height={96}
            sx={{ borderRadius: "8px", mb: 3 }}
          />
          <Skeleton
            variant="rounded"
            width="100%"
            height={176}
            sx={{ borderRadius: "8px", mb: 3 }}
          />
          {[1, 2, 3].map((i) => (
            <Box key={i} sx={{ mb: 2.5 }}>
              <SkeletonLine width={120} height={16} />
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                <SkeletonChip />
                <SkeletonChip width={96} />
              </Box>
            </Box>
          ))}
          <Box sx={{ mt: 1 }}>
            <SkeletonLine width={140} height={16} />
            {[1, 2, 3].map((i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                <Skeleton variant="circular" width={40} height={40} />
                <Box sx={{ flex: 1 }}>
                  <SkeletonLine width="55%" height={14} />
                  <SkeletonLine width="40%" height={12} />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/** Edit team page form + members. */
export function TeamEditPageSkeleton() {
  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 3, py: 3 }}>
      <Box sx={{ maxWidth: 632, width: "100%", mx: "auto" }}>
        <Skeleton variant="text" width={160} height={34} sx={{ borderRadius: "4px" }} />
        <Skeleton variant="text" width="70%" height={18} sx={{ borderRadius: "4px", mt: 0.5, mb: 3 }} />
        {[1, 2, 3, 4].map((i) => (
          <Box key={i} sx={{ mb: 2 }}>
            <SkeletonLine width={80} height={12} />
            <Skeleton variant="rounded" width="100%" height={i === 2 ? 96 : 40} sx={{ borderRadius: "8px", mt: 0.75 }} />
          </Box>
        ))}
        <Skeleton variant="text" width={140} height={28} sx={{ borderRadius: "4px", mt: 2, mb: 1.5 }} />
        <Skeleton variant="rounded" width="100%" height={40} sx={{ borderRadius: "8px", mb: 2 }} />
        {[1, 2, 3].map((i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              py: 1,
              borderBottom: "1px solid #EEF2F6",
            }}
          >
            <Skeleton variant="circular" width={40} height={40} />
            <Box sx={{ flex: 1 }}>
              <SkeletonLine width="50%" />
              <SkeletonLine width="65%" height={14} />
            </Box>
            <Skeleton variant="rounded" width={104} height={40} sx={{ borderRadius: "8px" }} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/** 280px detail sidebar — scroll body + footer (header rendered separately). */
export function TeamSidebarSkeleton() {
  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 1.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", py: 1.5, borderBottom: "1px solid #EEF2F6" }}>
          <Box sx={{ flex: 1, textAlign: "center" }}>
            <SkeletonLine width={48} height={12} />
            <SkeletonLine width={32} height={16} />
          </Box>
          <Box sx={{ flex: 1, textAlign: "center" }}>
            <SkeletonLine width={48} height={12} />
            <SkeletonLine width={32} height={16} />
          </Box>
        </Box>
        {[1, 2, 3].map((i) => (
          <Box key={i} sx={{ py: 1.5, borderBottom: "1px solid #EEF2F6" }}>
            <SkeletonLine width={80} height={16} />
            <Box sx={{ display: "flex", gap: 0.75, mt: 1 }}>
              <SkeletonChip />
              <SkeletonChip width={88} />
            </Box>
          </Box>
        ))}
        <Box sx={{ py: 1.5 }}>
          <SkeletonLine width={100} height={16} />
          {[1, 2].map((i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.75 }}>
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <SkeletonLine width="70%" height={12} />
                <SkeletonLine width="50%" height={12} />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      <Box sx={{ px: 2, py: 2, borderTop: "1px solid #EEF2F6", display: "flex", gap: "8px", flexShrink: 0 }}>
        <Skeleton variant="rounded" width={160} height={40} sx={{ borderRadius: "8px" }} />
        <Skeleton variant="rounded" width={80} height={40} sx={{ borderRadius: "8px" }} />
      </Box>
    </Box>
  );
}

/** Single form field placeholder (e.g. group select in create modal). */
export function TeamFormFieldSkeleton({ height = 40 }) {
  return (
    <Skeleton variant="rounded" width="100%" height={height} sx={{ borderRadius: "8px" }} />
  );
}
